import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/business/PageHeader";

const KIND_STYLE = {
  itr: "bg-blue-50 border-blue-300 text-blue-700",
  tds_return: "bg-purple-50 border-purple-300 text-purple-700",
  notice: "bg-rose-50 border-rose-300 text-rose-700",
  task: "bg-emerald-50 border-emerald-300 text-emerald-700",
};

const KIND_LABEL = { itr: "ITR", tds_return: "TDS", notice: "Notice", task: "Task" };

export default function Calendar() {
  const [view, setView] = useState("month");
  const [ref, setRef] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState({ from: "", to: "", events: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/calendar", { params: { view, ref } });
      setRows(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, ref]);

  const nav = (direction) => {
    const d = new Date(ref);
    if (view === "day") d.setDate(d.getDate() + direction);
    else if (view === "week") d.setDate(d.getDate() + 7 * direction);
    else d.setMonth(d.getMonth() + direction);
    setRef(d.toISOString().slice(0, 10));
  };

  const byDate = useMemo(() => {
    const m = {};
    (rows.events || []).forEach(e => { (m[e.due_date] = m[e.due_date] || []).push(e); });
    return m;
  }, [rows.events]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title eyebrow="Across all modules">Compliance Calendar</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground max-w-3xl">Every due date across ITR, TDS, notices and tasks — one view. Overdue items surface at the top.</p>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border overflow-hidden">
            {["day", "week", "month", "next30"].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wide ${view === v ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                data-testid={`view-${v}`}
              >{v}</button>
            ))}
          </div>
          <div className="inline-flex gap-1">
            <Button variant="outline" size="icon" onClick={() => nav(-1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" onClick={() => nav(1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {rows.from} → {rows.to} · <span className="font-semibold">{rows.events?.length || 0}</span> events
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          <Card className="border-border overflow-hidden">
            {Object.keys(byDate).length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No events in this range.</div>
            ) : (
              <div>
                {Object.entries(byDate).map(([date, evts]) => (
                  <div key={date} className="border-b border-border last:border-b-0">
                    <div className="bg-muted/40 px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold flex items-center justify-between">
                      <span>{date}</span>
                      <span className="text-muted-foreground normal-case tracking-normal font-normal">{evts.length} event(s)</span>
                    </div>
                    <ul className="divide-y divide-border">
                      {evts.map((e, i) => (
                        <li key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] uppercase font-semibold ${KIND_STYLE[e.kind] || "border-border"}`}>
                              {KIND_LABEL[e.kind] || e.kind}
                            </span>
                            <div className="min-w-0">
                              <div className="text-sm truncate">{e.title}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {e.source} · {e.priority} {e.status ? `· ${e.status}` : ""}
                              </div>
                            </div>
                          </div>
                          <div className={`text-xs whitespace-nowrap ${e.overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                            {e.overdue ? `${-e.days}d overdue` : `${e.days}d`}
                            {e.link && <Link to={e.link} className="ml-2 text-primary hover:underline">Open →</Link>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="space-y-4">
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Legend</div>
              <div className="mt-2 space-y-1">
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-xs">
                    <span className={`inline-block h-2.5 w-2.5 rounded ${KIND_STYLE[k]?.split(" ")[0]}`} />{v}
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5 border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Overdue</div>
              <ul className="mt-2 space-y-1.5">
                {(rows.events || []).filter(e => e.overdue).slice(0, 10).map((e, i) => (
                  <li key={i} className="text-xs rounded border border-rose-300 bg-rose-50 p-2">
                    <div className="font-semibold">{e.title}</div>
                    <div className="text-muted-foreground">{e.due_date} · {-e.days}d overdue</div>
                  </li>
                ))}
                {(rows.events || []).filter(e => e.overdue).length === 0 && <li className="text-xs text-muted-foreground">Nothing overdue.</li>}
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
